import { useMemo, type KeyboardEvent, type ReactNode } from "react";
// eslint-disable-next-line no-restricted-imports
import { Combobox as ComboboxBase } from "@base-ui/react/combobox";
// eslint-disable-next-line no-restricted-imports
import { Select as SelectBase } from "@base-ui/react/select";
import classNames from "classnames";
import {
    CheckboxIndicator,
    type CheckboxIndicatorState,
} from "@design-system/Checkbox/CheckboxIndicator";
import { unsupportedPickerKind, usePickerRoot } from "./PickerContext";
import { collectSelectableValues } from "./PickerTree";
import type { PickerItemValue, PickerSectionSelectAll } from "./Picker.types";
import styles from "./Picker.module.scss";

export interface PickerSectionProps {
    children: ReactNode;
    className?: string;
}

/** Base UI group: a titled run of items with the tighter 2px rhythm. */
export const PickerSection = ({ children, className }: PickerSectionProps) => {
    const root = usePickerRoot();
    const sectionClassName = classNames(
        styles.section,
        root.classNames.section,
        className,
    );

    switch (root.kind) {
        case "combobox":
            return (
                <ComboboxBase.Group className={sectionClassName}>
                    {children}
                </ComboboxBase.Group>
            );
        case "select":
            return (
                <SelectBase.Group className={sectionClassName}>
                    {children}
                </SelectBase.Group>
            );
        default:
            return unsupportedPickerKind(root.kind);
    }
};

export interface PickerSectionTitleProps<T extends PickerItemValue> {
    children: ReactNode;
    icon?: ReactNode;
    selectAll?: PickerSectionSelectAll<T>;
    className?: string;
}

const selectAllStateFor = ({
    enabledValues,
    selectedValues,
}: {
    enabledValues: readonly PickerItemValue[];
    selectedValues: readonly PickerItemValue[];
}): CheckboxIndicatorState => {
    const selectedSet = new Set(selectedValues);
    const selectedCount = enabledValues.filter((value) =>
        selectedSet.has(value),
    ).length;

    if (selectedCount === 0) {
        return "unchecked";
    }
    return selectedCount === enabledValues.length ? "checked" : "indeterminate";
};

/**
 * Section heading. With `selectAll` and a `multiple` root it renders the
 * trailing checkbox that toggles every enabled value of the section, nested
 * `items` descendants included.
 */
export const PickerSectionTitle = <T extends PickerItemValue>({
    children,
    icon,
    selectAll,
    className,
}: PickerSectionTitleProps<T>) => {
    const root = usePickerRoot();
    const sectionValues = useMemo(() => {
        if (selectAll === undefined) {
            return [];
        }
        return selectAll.items !== undefined
            ? collectSelectableValues({
                  items: selectAll.items,
                  selectableParents: root.isParentSelectable,
              })
            : selectAll.values;
    }, [selectAll, root.isParentSelectable]);
    const enabledValues = sectionValues.filter(
        (value) => !root.isValueDisabled(value),
    );
    const shouldRenderSelectAll =
        root.isMultiple && selectAll !== undefined && enabledValues.length > 0;
    const selectAllState = selectAllStateFor({
        enabledValues,
        selectedValues: root.selectedValues,
    });

    const toggleSection = () => {
        const enabledSet = new Set<PickerItemValue>(enabledValues);
        if (selectAllState === "checked") {
            root.setSelectedValues(
                root.selectedValues.filter((value) => !enabledSet.has(value)),
            );
            return;
        }
        const selectedSet = new Set(root.selectedValues);
        root.setSelectedValues([
            ...root.selectedValues,
            ...enabledValues.filter((value) => !selectedSet.has(value)),
        ]);
    };

    const content = (
        <>
            {icon !== undefined && (
                <span className={styles.sectionTitleIcon}>{icon}</span>
            )}
            <span
                className={styles.sectionTitleText}
                data-picker-part="section-title"
            >
                {children}
            </span>
            {shouldRenderSelectAll && (
                <button
                    type="button"
                    role="checkbox"
                    aria-checked={
                        selectAllState === "indeterminate"
                            ? "mixed"
                            : selectAllState === "checked"
                    }
                    aria-label="Select all in section"
                    disabled={root.isDisabled}
                    className={styles.sectionSelectAll}
                    onClick={toggleSection}
                    onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
                        // A real tab stop inside the popup: Enter and Space
                        // toggle here and stop, so the listbox neither
                        // selects its highlighted row nor closes.
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            event.stopPropagation();
                            toggleSection();
                        }
                    }}
                >
                    <CheckboxIndicator
                        state={selectAllState}
                        disabled={root.isDisabled}
                    />
                </button>
            )}
        </>
    );
    const titleClassName = classNames(styles.sectionTitle, className);

    switch (root.kind) {
        case "combobox":
            return (
                <ComboboxBase.GroupLabel className={titleClassName}>
                    {content}
                </ComboboxBase.GroupLabel>
            );
        case "select":
            return (
                <SelectBase.GroupLabel className={titleClassName}>
                    {content}
                </SelectBase.GroupLabel>
            );
        default:
            return unsupportedPickerKind(root.kind);
    }
};

export interface PickerSeparatorProps {
    className?: string;
}

/** Full-bleed 1px divider between sections. */
export const PickerSeparator = ({ className }: PickerSeparatorProps) => {
    const root = usePickerRoot();
    const separatorClassName = classNames(styles.separator, className);

    switch (root.kind) {
        case "combobox":
            return (
                <ComboboxBase.Separator
                    className={separatorClassName}
                    data-picker-part="separator"
                />
            );
        case "select":
            return (
                <SelectBase.Separator
                    className={separatorClassName}
                    data-picker-part="separator"
                />
            );
        default:
            return unsupportedPickerKind(root.kind);
    }
};
